import React, { useEffect, useState, useRef, useMemo } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import SeatManager from "../components/SeatManager";
import {
  Home,
  Menu,
  X,
  ChevronLeft,
  LogOut,
  User,
  Users,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  ShieldCheck,
  ExternalLink,
  ChevronRight,
  ArrowUpRight,
  BookOpen,
  Code2,
  LifeBuoy,
} from "lucide-react";
import { services } from "@/config/services";
import { useAuth } from "../lib/auth";
import "../styles/dashboard.css";
const ACTIVE_SERVICE_STORAGE_KEY = "adx_hub_active_service";
const SIDEBAR_OPEN_STORAGE_KEY = "adx_hub_sidebar_open";
const AUTH_PANEL_SERVICE_ID = "idp-auth";
const SEATS_SERVICE_ID = "seats";

const readStoredSidebarOpen = (fallbackValue = true) => {
  if (typeof window === "undefined") return fallbackValue;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    /* ignore storage failures */
  }
  return fallbackValue;
};

const toSameOriginUrl = (configuredUrl, fallbackPath) => {
  const raw = (configuredUrl || "").trim();
  if (!raw) return fallbackPath;
  if (raw.startsWith("/")) return raw;
  if (typeof window === "undefined") return raw;

  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return fallbackPath;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallbackPath;
  }
};

const joinServiceUrl = (baseUrl, subpath) => {
  const base = (baseUrl || "").replace(/\/$/, "");
  if (!subpath) return base;
  return `${base}${subpath.startsWith("/") ? subpath : `/${subpath}`}`;
};

const embeddedServiceErrorMessage = (serviceName, status) => {
  if (typeof status === "number") {
    return `${serviceName} gateway returned HTTP ${status}.`;
  }
  return `${serviceName} is temporarily unavailable.`;
};

const preflightEmbeddedService = async ({
  serviceName,
  baseUrl,
  subpath,
  signal,
  acceptableStatuses = [],
}) => {
  const url = joinServiceUrl(baseUrl, subpath);
  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal,
      headers: {
        Accept: "application/json,text/plain,*/*",
      },
    });
    if (acceptableStatuses.includes(response.status)) {
      return {
        ok: true,
        status: response.status,
        url,
      };
    }
    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        url,
        message: embeddedServiceErrorMessage(serviceName, response.status),
      };
    }
    return {
      ok: true,
      url,
    };
  } catch (error) {
    if (signal?.aborted) {
      return {
        ok: false,
        aborted: true,
        url,
      };
    }
    return {
      ok: false,
      url,
      message: embeddedServiceErrorMessage(serviceName),
      cause: error,
    };
  }
};

const resolveInitialActiveService = () => {
  if (typeof window === "undefined") return "home";

  const params = new URLSearchParams(window.location.search);
  if (params.get("login") === "1" || params.get("error")) {
    return AUTH_PANEL_SERVICE_ID;
  }

  try {
    const stored = window.localStorage.getItem(ACTIVE_SERVICE_STORAGE_KEY);
    const validIds = new Set(["home", "docs", "apidocs", SEATS_SERVICE_ID, ...services.map((s) => s.id)]);
    return stored && validIds.has(stored) ? stored : "home";
  } catch {
    return "home";
  }
};

function Dashboard() {
  const { user, logout, refresh, loading } = useAuth();
  const isAuthenticated = Boolean(user);
  const userInfo = user;
  const editorEmbedAuthToken = useMemo(
    () => userInfo?.jwt || userInfo?.token || userInfo?.access_token || null,
    [userInfo?.jwt, userInfo?.token, userInfo?.access_token],
  );
  const editorEmbedDevIdentity = useMemo(
    () =>
      [userInfo?.email, userInfo?.id, userInfo?.username].filter(Boolean).join("|") || "",
    [userInfo?.email, userInfo?.id, userInfo?.username],
  );
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 768;
  });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    if (window.innerWidth < 768) return false;
    return readStoredSidebarOpen(true);
  });
  const [activeService, setActiveService] = useState(resolveInitialActiveService);
  const smtContainerRef = useRef(null);
  const smtMountRef = useRef(null);
  const [smtLoaded, setSmtLoaded] = useState(false);
  const [smtError, setSmtError] = useState(null);
  const discoveryContainerRef = useRef(null);
  const discoveryMountRef = useRef(null);
  const [discoveryLoaded, setDiscoveryLoaded] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(null);
  const discoveryClientContainerRef = useRef(null);
  const discoveryClientMountRef = useRef(null);
  const [discoveryClientLoaded, setDiscoveryClientLoaded] = useState(false);
  const [discoveryClientError, setDiscoveryClientError] = useState(null);
  const validationContainerRef = useRef(null);
  const validationMountRef = useRef(null);
  const [validationLoaded, setValidationLoaded] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const editorContainerRef = useRef(null);
  const editorMountRef = useRef(null);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [editorError, setEditorError] = useState(null);
  const [fxHealth, setFxHealth] = useState({
    loaded: false,
    hasSubjectToken: true,
    stsReachable: true,
  });
  const [idpAuthMethod, setIdpAuthMethod] = useState("basic");
  const [idpUsername, setIdpUsername] = useState("");
  const [idpPassword, setIdpPassword] = useState("");
  const [idpShowPassword, setIdpShowPassword] = useState(false);
  const [authPanelError, setAuthPanelError] = useState("");
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordMessage, setChangePasswordMessage] = useState("");
  const [changePasswordSubmitting, setChangePasswordSubmitting] = useState(false);
  const [isServiceSwitching, setIsServiceSwitching] = useState(false);
  const wasDesktopViewportRef = useRef(isDesktopViewport);
  const previousServiceRef = useRef(activeService);
  const discoveryClientBundleVersionRef = useRef(Date.now());
  const prefersReducedMotion = useReducedMotion();

  const discoveryBundleUrl = toSameOriginUrl(
    process.env.REACT_APP_DISCOVERY_BUNDLE_URL,
    "/api/discovery/static/frontend/discovery-bundle.js"
  );
  const discoveryApiUrl = toSameOriginUrl(
    process.env.REACT_APP_DISCOVERY_API_URL,
    "/api/discovery"
  );
  const smtBundleUrl = toSameOriginUrl(
    process.env.REACT_APP_SMT_BUNDLE_URL,
    "/api/smt-repo/static/frontend/smt-repo-app.js"
  );
  const smtApiUrl = toSameOriginUrl(
    process.env.REACT_APP_SMT_API_URL,
    "/api/smt-repo"
  ).replace(/\/$/, "");
  const discoveryClientBundleUrl = toSameOriginUrl(
    process.env.REACT_APP_DISCOVERY_CLIENT_BUNDLE_URL,
    "/api/discovery-client/static/js/discovery-client-bundle.js"
  );
  const discoveryClientApiUrl = toSameOriginUrl(
    process.env.REACT_APP_DISCOVERY_CLIENT_API_URL,
    "/api/discovery-client"
  );
  const idpUrl = process.env.REACT_APP_IDP_URL || "https://idp.adx-hub.com";
  const authReturnTarget = "/dashboard";
  const basicAuthActionUrl = `/auth/start?provider=basic&next=${encodeURIComponent(authReturnTarget)}`;
  const microsoftAuthUrl = `/auth/start?provider=microsoft&next=${encodeURIComponent(authReturnTarget)}`;
  const validationBundleUrl = toSameOriginUrl(
    process.env.REACT_APP_VALIDATION_BUNDLE_URL,
    "/validation-service/static/frontend/validation-bundle.js"
  );
  const validationApiUrl = toSameOriginUrl(
    process.env.REACT_APP_VALIDATION_API_URL,
    "/validation-service/api/react"
  );
  const validationCssUrl = toSameOriginUrl(
    process.env.REACT_APP_VALIDATION_CSS_URL,
    "/validation-service/static/frontend/style.css"
  );
  const editorBundleUrl = toSameOriginUrl(
    process.env.REACT_APP_EDITOR_BUNDLE_URL,
    "/api/aas-editor/aas-editor-embed.umd.js"
  );
  const editorApiUrl = toSameOriginUrl(
    process.env.REACT_APP_EDITOR_API_URL,
    "/api/aas-editor"
  ).replace(/\/$/, "");
  const editorCssUrl = toSameOriginUrl(
    process.env.REACT_APP_EDITOR_CSS_URL,
    "/api/aas-editor/aas-editor.css"
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const controller = new AbortController();
    let cancelled = false;

    const probeFxHealth = async () => {
      try {
        const response = await fetch("/api/fx/status", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        setFxHealth({
          loaded: true,
          hasSubjectToken: Boolean(data?.has_subject_token),
          stsReachable: Boolean(data?.sts_reachable),
        });
      } catch (_error) {
        if (cancelled) return;
        setFxHealth({
          loaded: true,
          hasSubjectToken: true,
          stsReachable: false,
        });
      }
    };

    if (!isAuthenticated) {
      setFxHealth({
        loaded: true,
        hasSubjectToken: false,
        stsReachable: false,
      });
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    probeFxHealth();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const persistableService = activeService === AUTH_PANEL_SERVICE_ID ? "home" : activeService;
      window.localStorage.setItem(ACTIVE_SERVICE_STORAGE_KEY, persistableService);
    } catch {
      /* ignore storage failures */
    }
  }, [activeService]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncViewportState = () => {
      const nextIsDesktop = window.innerWidth >= 768;
      setIsDesktopViewport(nextIsDesktop);
      setSidebarOpen((prev) => {
        if (!nextIsDesktop) return false;
        if (!wasDesktopViewportRef.current && nextIsDesktop) return readStoredSidebarOpen(true);
        return prev;
      });
      wasDesktopViewportRef.current = nextIsDesktop;
    };
    syncViewportState();
    window.addEventListener("resize", syncViewportState);
    return () => window.removeEventListener("resize", syncViewportState);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !isDesktopViewport) return;
    try {
      window.localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, sidebarOpen ? "1" : "0");
    } catch {
      /* ignore storage failures */
    }
  }, [isDesktopViewport, sidebarOpen]);

  // Open the sign-in panel once per unauthenticated visit to /dashboard?login=1.
  // The flag has to stay in the URL - RequireAuth reads it to allow an
  // unauthenticated render instead of redirecting to /auth/start - so the guard
  // is a ref rather than removing the parameter. Without it this effect re-ran
  // on later renders and reopened the panel after the user had switched away
  // from it, which is why the login dialog appeared a second time.
  const loginPanelOpenedRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isAuthenticated) {
      loginPanelOpenedRef.current = false;
      return;
    }
    if (loginPanelOpenedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("login") !== "1") return;

    loginPanelOpenedRef.current = true;
    setActiveService(AUTH_PANEL_SERVICE_ID);
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window === "undefined" || loading) return;

    if (isAuthenticated) {
      if (activeService === AUTH_PANEL_SERVICE_ID) {
        setActiveService("home");
      }
      const url = new URL(window.location.href);
      if (url.searchParams.has("login") || url.searchParams.has("error")) {
        url.searchParams.delete("login");
        url.searchParams.delete("error");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
      return;
    }

    const protectedServiceIds = new Set(["docs", "apidocs", SEATS_SERVICE_ID, ...services.map((service) => service.id)]);
    if (protectedServiceIds.has(activeService) && activeService !== AUTH_PANEL_SERVICE_ID) {
      setAuthPanelError(
        activeService === "docs"
          ? "Please sign in to unlock documentation."
          : "Please sign in to unlock services.",
      );
      setActiveService(AUTH_PANEL_SERVICE_ID);
    }
  }, [activeService, isAuthenticated, loading]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get("error");
    if (!errorCode) {
      setAuthPanelError("");
      return;
    }
    const messageByCode = {
      missing_credentials: "Please enter username and password.",
      invalid_credentials: "Invalid username or password. Please try again.",
      auth_failed: "Authentication failed. Please sign in again.",
    };
    setAuthPanelError(messageByCode[errorCode] || "Sign in failed. Please try again.");
    setActiveService(AUTH_PANEL_SERVICE_ID);
  }, []);

  useEffect(() => {
    if (previousServiceRef.current === activeService) return;
    previousServiceRef.current = activeService;
    setIsServiceSwitching(true);
    const timeoutId = window.setTimeout(() => {
      setIsServiceSwitching(false);
    }, prefersReducedMotion ? 120 : 360);
    return () => window.clearTimeout(timeoutId);
  }, [activeService, prefersReducedMotion]);

  const handleLogin = () => {
    setActiveService(AUTH_PANEL_SERVICE_ID);
  };

  const handleLogout = () => {
    logout();
  };

  const handleServiceClick = (service) => {
    if (!isAuthenticated) {
      setAuthPanelError("Please sign in to unlock services.");
      setActiveService(AUTH_PANEL_SERVICE_ID);
      return;
    }
    setActiveService(service.id);
  };

  const handleSeatsClick = () => {
    if (!isAuthenticated) {
      setActiveService(AUTH_PANEL_SERVICE_ID);
      return;
    }
    setActiveService(SEATS_SERVICE_ID);
    setSidebarOpen(false);
  };

  const handleDocsClick = () => {
    if (!isAuthenticated) {
      setAuthPanelError("Please sign in to unlock documentation.");
      setActiveService(AUTH_PANEL_SERVICE_ID);
      return;
    }
    setActiveService("docs");
  };

  const handleApiDocsClick = () => {
    if (!isAuthenticated) {
      setAuthPanelError("Please sign in to unlock the API documentation.");
      setActiveService(AUTH_PANEL_SERVICE_ID);
      return;
    }
    setActiveService("apidocs");
  };

  const handleChangePasswordInput = (field, value) => {
    setChangePasswordForm((prev) => ({ ...prev, [field]: value }));
    setChangePasswordError("");
    setChangePasswordMessage("");
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();
    setChangePasswordError("");
    setChangePasswordMessage("");

    const { currentPassword, newPassword, confirmPassword } = changePasswordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setChangePasswordError("Please fill in all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError("New password and confirmation do not match.");
      return;
    }

    setChangePasswordSubmitting(true);
    try {
      const response = await fetch("/api/account/password/change", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setChangePasswordError(data.message || "Could not change password. Please try again.");
        return;
      }
      setChangePasswordMessage(data.message || "Password updated successfully.");
      setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (_error) {
      setChangePasswordError("Password update failed. Please try again.");
    } finally {
      setChangePasswordSubmitting(false);
    }
  };

  const renderServiceNavIcon = (service, isActive) => {
    if (service.iconImage) {
      return (
        <span className={`dashboard-nav-icon-wrap flex h-6 w-6 items-center justify-center rounded ${isActive ? 'bg-white/95' : ''}`}>
          <img
            src={service.iconImage}
            alt=""
            aria-hidden="true"
            className="dashboard-nav-icon-img h-5 w-5 object-contain"
          />
        </span>
      );
    }
    const Icon = service.icon;
    return (
      <span className="dashboard-nav-icon-wrap flex h-6 w-6 items-center justify-center rounded">
        <Icon size={20} className="dashboard-nav-icon-svg" />
      </span>
    );
  };

  const resolveEditorMount = () => {
    if (typeof window === "undefined") return null;
    if (typeof window.mountAasEditor === "function") return window.mountAasEditor;
    if (window.AasEditorEmbed && typeof window.AasEditorEmbed.mountAasEditor === "function") {
      return window.AasEditorEmbed.mountAasEditor;
    }
    return null;
  };

  useEffect(() => {
    if (activeService !== "smt") {
      if (smtMountRef.current?.unmount) {
        smtMountRef.current.unmount();
        smtMountRef.current = null;
      }
      setSmtLoaded(false);
      setSmtError(null);
      return;
    }

    let cancelled = false;
    let mountRetryTimeout = null;
    let mountRetryAttempts = 0;
    let script = null;
    let existingScript = null;
    const maxMountRetryAttempts = 24;
    setSmtLoaded(false);
    setSmtError(null);

    const fxBlocked = fxHealth.loaded && (!fxHealth.hasSubjectToken || !fxHealth.stsReachable);
    if (fxBlocked) {
      const message = !fxHealth.hasSubjectToken
        ? "Authentication required to access SMT Repository."
        : "Federated token service unavailable. SMT Repository is temporarily unavailable.";
      setSmtError(message);
      return;
    }

    const preflightController = new AbortController();

    const applySmtApiBase = () => {
      if (typeof window === "undefined") return;
      window.__SMT_REPO_API_BASE__ = smtApiUrl;
      window.__PORTAL_API_BASE__ = smtApiUrl;
      window.__SMT_IDP_URL__ = idpUrl;
      window.__SMT_EMBEDDED__ = true;
    };

    const mountIfReady = () => {
      if (cancelled || !smtContainerRef.current || typeof window.mountSmt !== "function") {
        return false;
      }
      applySmtApiBase();
      if (smtMountRef.current?.unmount) {
        smtMountRef.current.unmount();
        smtMountRef.current = null;
      }
      const mountHandle = window.mountSmt(smtContainerRef.current, "/dashboard", { embedded: true });
      smtMountRef.current = mountHandle || null;
      if (!cancelled) {
        setSmtLoaded(true);
        setSmtError(null);
      }
      return true;
    };

    const scheduleMountRetry = (sourceUrl) => {
      if (cancelled || mountIfReady()) return;
      if (mountRetryAttempts >= maxMountRetryAttempts) {
        if (!cancelled) {
          setSmtError(sourceUrl || smtBundleUrl);
          console.error("Failed to mount SMT bundle after retries from", sourceUrl || smtBundleUrl);
        }
        return;
      }
      mountRetryAttempts += 1;
      mountRetryTimeout = window.setTimeout(() => {
        scheduleMountRetry(sourceUrl || smtBundleUrl);
      }, 160);
    };

    const onScriptLoad = () => {
      scheduleMountRetry(smtBundleUrl);
    };
    const onScriptError = () => {
      if (cancelled) return;
      setSmtError(smtBundleUrl);
      console.error("Failed to load SMT bundle script from", smtBundleUrl);
    };

    const initializeSmt = async () => {
      const probe = await preflightEmbeddedService({
        serviceName: "SMT Repository",
        baseUrl: smtApiUrl,
        subpath: "/api/v3.0/submodels",
        signal: preflightController.signal,
      });
      if (cancelled || probe.aborted) {
        return;
      }
      if (!probe.ok) {
        setSmtError(probe.message);
        console.error("SMT Repository preflight failed", probe.url, probe.status || probe.cause || "unreachable");
        return;
      }

      if (typeof window.mountSmt === "function") {
        scheduleMountRetry(smtBundleUrl);
        return;
      }

      existingScript = document.querySelector('script[data-smt-bundle="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", onScriptLoad);
        existingScript.addEventListener("error", onScriptError);
        scheduleMountRetry(smtBundleUrl);
        return;
      }

      script = document.createElement("script");
      script.src = smtBundleUrl;
      script.async = true;
      script.dataset.smtBundle = "true";
      script.addEventListener("load", onScriptLoad);
      script.addEventListener("error", onScriptError);
      document.body.appendChild(script);
    };

    void initializeSmt();

    return () => {
      cancelled = true;
      preflightController.abort();
      if (mountRetryTimeout) {
        window.clearTimeout(mountRetryTimeout);
      }
      if (existingScript) {
        existingScript.removeEventListener("load", onScriptLoad);
        existingScript.removeEventListener("error", onScriptError);
      }
      if (script) {
        script.removeEventListener("load", onScriptLoad);
        script.removeEventListener("error", onScriptError);
      }
      if (smtMountRef.current?.unmount) {
        smtMountRef.current.unmount();
        smtMountRef.current = null;
      }
    };
  }, [activeService, smtBundleUrl, smtApiUrl, idpUrl, fxHealth]);

  useEffect(() => {
    if (activeService !== "discovery") {
      if (discoveryMountRef.current?.unmount) {
        discoveryMountRef.current.unmount();
        discoveryMountRef.current = null;
      }
      setDiscoveryLoaded(false);
      setDiscoveryError(null);
      return;
    }

    let cancelled = false;
    let retryTimeout = null;
    let retryAttempts = 0;
    let script = null;
    let bundleBlobUrl = null;
    let currentBundleIndex = 0;
    let activeBundleUrl = discoveryBundleUrl;
    const preflightController = new AbortController();
    const maxRetryAttempts = 18;
    const bundleCandidates = Array.from(
      new Set([
        discoveryBundleUrl,
        "/api/discovery/static/frontend/discovery-bundle.js",
      ].filter(Boolean))
    );
    setDiscoveryLoaded(false);
    setDiscoveryError(null);

    const fxBlocked = fxHealth.loaded && (!fxHealth.hasSubjectToken || !fxHealth.stsReachable);
    if (fxBlocked) {
      const message = !fxHealth.hasSubjectToken
        ? "Authentication required to access Discovery."
        : "Federated token service unavailable. Discovery is temporarily unavailable.";
      setDiscoveryError(message);
      return;
    }

    if (typeof window !== "undefined") {
      window.__DISCOVERY_API_URL__ = discoveryApiUrl;
      window.__DISCOVERY_IDP_URL__ = idpUrl;
      window.__DISCOVERY_EMBEDDED__ = true;
      window.__ADX_HUB_OPEN_AAS_VIEWER__ = () => setActiveService("aas-viewer");
    }
    const mountIfReady = () => {
      if (cancelled || !discoveryContainerRef.current || typeof window.mountDiscovery !== "function") {
        return false;
      }
      if (discoveryMountRef.current?.unmount) {
        discoveryMountRef.current.unmount();
        discoveryMountRef.current = null;
      }
      const mountHandle = window.mountDiscovery(discoveryContainerRef.current, "/dashboard", { embedded: true });
      discoveryMountRef.current = mountHandle || null;
      if (!cancelled) {
        setDiscoveryLoaded(true);
        setDiscoveryError(null);
      }
      return true;
    };

    const cleanupScript = () => {
      if (!script) return;
      script.onload = null;
      script.onerror = null;
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      script = null;
      if (bundleBlobUrl) {
        URL.revokeObjectURL(bundleBlobUrl);
        bundleBlobUrl = null;
      }
    };

    const scheduleMountRetry = (sourceUrl) => {
      if (cancelled || mountIfReady()) return;
      if (retryAttempts >= maxRetryAttempts) {
        setDiscoveryError(sourceUrl || activeBundleUrl);
        console.error("Failed to mount Discovery bundle after retries from", sourceUrl || activeBundleUrl);
        return;
      }
      retryAttempts += 1;
      retryTimeout = window.setTimeout(() => {
        scheduleMountRetry(sourceUrl || activeBundleUrl);
      }, 180);
    };

    const tryLoadBundle = async () => {
      if (cancelled) return;
      if (currentBundleIndex >= bundleCandidates.length) {
        setDiscoveryError(activeBundleUrl);
        console.error("Failed to load Discovery bundle script from all candidates", bundleCandidates);
        return;
      }

      activeBundleUrl = bundleCandidates[currentBundleIndex];
      let source = "";
      try {
        const response = await fetch(activeBundleUrl, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/javascript,text/javascript,*/*;q=0.8",
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const contentType = (response.headers.get("content-type") || "").toLowerCase();
        source = await response.text();
        const trimmed = source.trimStart().toLowerCase();
        const looksLikeHtml =
          contentType.includes("text/html") ||
          trimmed.startsWith("<!doctype") ||
          trimmed.startsWith("<html") ||
          trimmed.startsWith("<");
        if (looksLikeHtml) {
          throw new Error("Received HTML instead of JavaScript bundle");
        }
      } catch (error) {
        currentBundleIndex += 1;
        if (currentBundleIndex < bundleCandidates.length) {
          if (!cancelled) {
            tryLoadBundle();
          }
          return;
        }
        setDiscoveryError(activeBundleUrl);
        console.error("Failed to fetch Discovery bundle script from", activeBundleUrl, error);
        return;
      }

      if (cancelled) return;
      cleanupScript();
      bundleBlobUrl = URL.createObjectURL(new Blob([source], { type: "application/javascript" }));
      script = document.createElement("script");
      script.src = bundleBlobUrl;
      script.async = true;
      script.dataset.discoveryBundle = "true";
      script.onload = () => {
        if (cancelled) return;
        scheduleMountRetry(activeBundleUrl);
      };
      script.onerror = () => {
        if (cancelled) return;
        cleanupScript();
        currentBundleIndex += 1;
        if (currentBundleIndex < bundleCandidates.length) {
          tryLoadBundle();
          return;
        }
        setDiscoveryError(activeBundleUrl);
        console.error("Failed to load Discovery bundle script from", activeBundleUrl);
      };
      document.body.appendChild(script);
    };

    const initializeDiscovery = async () => {
      const probe = await preflightEmbeddedService({
        serviceName: "Discovery",
        baseUrl: discoveryApiUrl,
        subpath: "/api/idp/session-check",
        signal: preflightController.signal,
      });
      if (cancelled || probe.aborted) {
        return;
      }
      if (!probe.ok) {
        setDiscoveryError(probe.message);
        console.error("Discovery preflight failed", probe.url, probe.status || probe.cause || "unreachable");
        return;
      }

      if (typeof window.mountDiscovery === "function") {
        scheduleMountRetry(activeBundleUrl);
        return;
      }

      document
        .querySelectorAll('script[data-discovery-bundle="true"]')
        .forEach((node) => node.parentNode && node.parentNode.removeChild(node));
      tryLoadBundle();
    };

    void initializeDiscovery();

    return () => {
      cancelled = true;
      preflightController.abort();
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
      cleanupScript();
      if (discoveryMountRef.current?.unmount) {
        discoveryMountRef.current.unmount();
        discoveryMountRef.current = null;
      }
    };
  }, [activeService, discoveryBundleUrl, discoveryApiUrl, idpUrl, fxHealth]);

  useEffect(() => {
    if (activeService !== "aas-viewer") {
      if (discoveryClientMountRef.current?.unmount) {
        discoveryClientMountRef.current.unmount();
        discoveryClientMountRef.current = null;
      }
      setDiscoveryClientLoaded(false);
      setDiscoveryClientError(null);
      return;
    }

    let cancelled = false;
    let mountRetryTimeout = null;
    let mountRetryAttempts = 0;
    let script = null;
    let existingScript = null;
    const maxMountRetryAttempts = 24;
    setDiscoveryClientLoaded(false);
    setDiscoveryClientError(null);

    const fxBlocked = fxHealth.loaded && (!fxHealth.hasSubjectToken || !fxHealth.stsReachable);
    if (fxBlocked) {
      const message = !fxHealth.hasSubjectToken
        ? "Authentication required to access AAS Viewer."
        : "Federated token service unavailable. AAS Viewer is temporarily unavailable.";
      setDiscoveryClientError(message);
      return;
    }

    const preflightController = new AbortController();

    if (typeof window !== "undefined") {
      window.__DISCOVERY_CLIENT_API_URL__ = discoveryClientApiUrl;
      window.__DISCOVERY_CLIENT_IDP_URL__ = idpUrl;
      window.__DISCOVERY_CLIENT_EMBEDDED__ = true;
    }
    const mountIfReady = () => {
      if (cancelled || !discoveryClientContainerRef.current || typeof window.mountDiscoveryClient !== "function") {
        return false;
      }
      if (discoveryClientMountRef.current?.unmount) {
        discoveryClientMountRef.current.unmount();
        discoveryClientMountRef.current = null;
      }
      const mountHandle = window.mountDiscoveryClient(discoveryClientContainerRef.current, "/dashboard", {
        embedded: true,
        apiBaseUrl: discoveryClientApiUrl
      });
      discoveryClientMountRef.current = mountHandle || null;
      if (!cancelled) {
        setDiscoveryClientLoaded(true);
      }
      return true;
    };

    const scheduleMountRetry = (sourceUrl) => {
      if (cancelled || mountIfReady()) return;
      if (mountRetryAttempts >= maxMountRetryAttempts) {
        if (!cancelled) {
          setDiscoveryClientError(sourceUrl || discoveryClientBundleUrl);
          console.error("Failed to mount Discovery Client bundle after retries from", sourceUrl || discoveryClientBundleUrl);
        }
        return;
      }
      mountRetryAttempts += 1;
      mountRetryTimeout = window.setTimeout(() => {
        scheduleMountRetry(sourceUrl || discoveryClientBundleUrl);
      }, 160);
    };

    const onScriptLoad = () => {
      scheduleMountRetry(discoveryClientBundleUrl);
    };
    const onScriptError = () => {
      if (cancelled) return;
      setDiscoveryClientError(discoveryClientBundleUrl);
      console.error("Failed to load Discovery Client bundle script from", discoveryClientBundleUrl);
    };

    const initializeDiscoveryClient = async () => {
      const probe = await preflightEmbeddedService({
        serviceName: "AAS Viewer",
        baseUrl: discoveryClientApiUrl,
        subpath: "/api/frontend/config",
        signal: preflightController.signal,
      });
      if (cancelled || probe.aborted) {
        return;
      }
      if (!probe.ok) {
        setDiscoveryClientError(probe.message);
        console.error("Discovery Client preflight failed", probe.url, probe.status || probe.cause || "unreachable");
        return;
      }

      if (typeof window.mountDiscoveryClient === "function") {
        scheduleMountRetry(discoveryClientBundleUrl);
        return;
      }

      const separator = discoveryClientBundleUrl.includes("?") ? "&" : "?";
      const desiredBundleUrl = `${discoveryClientBundleUrl}${separator}v=${discoveryClientBundleVersionRef.current}`;
      existingScript = document.querySelector('script[data-discovery-client-bundle="true"]');
      if (existingScript) {
        const existingSrc = existingScript.getAttribute("src") || "";
        if (existingSrc !== desiredBundleUrl) {
          existingScript.remove();
          existingScript = null;
        }
      }

      if (existingScript) {
        existingScript.addEventListener("load", onScriptLoad);
        existingScript.addEventListener("error", onScriptError);
        scheduleMountRetry(discoveryClientBundleUrl);
        return;
      }

      script = document.createElement("script");
      script.src = desiredBundleUrl;
      script.async = true;
      script.dataset.discoveryClientBundle = "true";
      script.addEventListener("load", onScriptLoad);
      script.addEventListener("error", onScriptError);
      document.body.appendChild(script);
    };

    void initializeDiscoveryClient();

    return () => {
      cancelled = true;
      preflightController.abort();
      if (mountRetryTimeout) {
        window.clearTimeout(mountRetryTimeout);
      }
      if (existingScript) {
        existingScript.removeEventListener("load", onScriptLoad);
        existingScript.removeEventListener("error", onScriptError);
      }
      if (script) {
        script.removeEventListener("load", onScriptLoad);
        script.removeEventListener("error", onScriptError);
      }
      if (discoveryClientMountRef.current?.unmount) {
        discoveryClientMountRef.current.unmount();
        discoveryClientMountRef.current = null;
      }
    };
  }, [activeService, discoveryClientBundleUrl, discoveryClientApiUrl, idpUrl, fxHealth]);

  useEffect(() => {
    if (activeService !== "validation") {
      if (validationMountRef.current?.unmount) {
        validationMountRef.current.unmount();
        validationMountRef.current = null;
      }
      setValidationLoaded(false);
      setValidationError(null);
      return;
    }

    let cancelled = false;
    let mountRetryTimeout = null;
    let mountRetryAttempts = 0;
    const maxMountRetryAttempts = 28;
    setValidationLoaded(false);
    setValidationError(null);

    if (typeof window !== "undefined") {
      window.__VALIDATION_API_URL__ = validationApiUrl;
      window.__VALIDATION_IDP_URL__ = idpUrl;
      window.__VALIDATION_EMBEDDED__ = true;
    }

    const mountIfReady = () => {
      if (cancelled || !validationContainerRef.current || typeof window.mountValidation !== "function") {
        return false;
      }
      if (validationMountRef.current?.unmount) {
        validationMountRef.current.unmount();
        validationMountRef.current = null;
      }
      const mountHandle = window.mountValidation(validationContainerRef.current, "/validation-service", {
        embedded: true,
        apiBaseUrl: validationApiUrl,
        cssUrl: validationCssUrl
      });
      validationMountRef.current = mountHandle || null;
      if (!cancelled) {
        setValidationLoaded(true);
        setValidationError(null);
      }
      return true;
    };

    const scheduleMountRetry = (sourceUrl) => {
      if (cancelled || mountIfReady()) return;
      if (mountRetryAttempts >= maxMountRetryAttempts) {
        if (!cancelled) {
          setValidationError(sourceUrl || validationBundleUrl);
          console.error("Failed to mount Validation bundle after retries from", sourceUrl || validationBundleUrl);
        }
        return;
      }
      mountRetryAttempts += 1;
      mountRetryTimeout = window.setTimeout(() => {
        scheduleMountRetry(sourceUrl || validationBundleUrl);
      }, 170);
    };

    // Always reload validation bundle on activation to avoid stale in-memory/cached script.
    document.querySelectorAll('script[data-validation-bundle="true"]').forEach((node) => node.remove());
    if (typeof window !== "undefined") {
      delete window.mountValidation;
    }

    const script = document.createElement("script");
    const separator = validationBundleUrl.includes("?") ? "&" : "?";
    script.src = `${validationBundleUrl}${separator}v=${Date.now()}`;
    script.async = true;
    script.dataset.validationBundle = "true";
    const onScriptLoad = () => {
      scheduleMountRetry(validationBundleUrl);
    };
    const onScriptError = () => {
      if (cancelled) return;
      setValidationError(validationBundleUrl);
      console.error("Failed to load Validation bundle script from", validationBundleUrl);
    };
    script.addEventListener("load", onScriptLoad);
    script.addEventListener("error", onScriptError);
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      if (mountRetryTimeout) {
        window.clearTimeout(mountRetryTimeout);
      }
      script.removeEventListener("load", onScriptLoad);
      script.removeEventListener("error", onScriptError);
      if (validationMountRef.current?.unmount) {
        validationMountRef.current.unmount();
        validationMountRef.current = null;
      }
    };
  }, [activeService, validationBundleUrl, validationApiUrl, validationCssUrl, idpUrl]);

  useEffect(() => {
    if (activeService !== "editor") {
      if (editorMountRef.current?.unmount) {
        editorMountRef.current.unmount();
        editorMountRef.current = null;
      }
      setEditorLoaded(false);
      setEditorError(null);
      return;
    }

    let cancelled = false;
    let mountRetryTimeout = null;
    let mountRetryAttempts = 0;
    const maxMountRetryAttempts = 24;
    setEditorLoaded(false);
    setEditorError(null);

    window.__AAS_EDITOR_API_URL__ = editorApiUrl;
    window.__AAS_EDITOR_IDP_URL__ = idpUrl;
    window.__AAS_EDITOR_EMBEDDED__ = true;

    const ensureCss = () => {
      if (!editorCssUrl) return;
      const existingCss = document.querySelector('link[data-editor-css="true"]');
      if (existingCss) return;

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = editorCssUrl;
      link.dataset.editorCss = "true";
      link.onerror = () => {
        console.error("Failed to load AAS Editor CSS from", editorCssUrl);
      };
      document.head.appendChild(link);
    };

    const mountIfReady = () => {
      if (cancelled) {
        return false;
      }
      const mountAasEditor = resolveEditorMount();
      if (!mountAasEditor || !editorContainerRef.current) {
        return false;
      }

      if (editorMountRef.current?.unmount) {
        editorMountRef.current.unmount();
        editorMountRef.current = null;
      }

      const bearerToken = editorEmbedAuthToken || undefined;
      const isLocalDevHost =
        window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const devUserId =
        userInfo?.email ||
        userInfo?.id ||
        userInfo?.username ||
        "adx-hub-user";

      const mountHandle = mountAasEditor(editorContainerRef.current, {
        backendBaseUrl: editorApiUrl,
        initialPath: "/dashboard",
        bearerToken,
        devUserId: bearerToken || !isLocalDevHost ? undefined : devUserId,
      });

      editorMountRef.current = mountHandle || null;
      if (!cancelled) {
        setEditorLoaded(true);
        setEditorError(null);
      }
      return true;
    };

    const scheduleMountRetry = (sourceUrl) => {
      if (cancelled || mountIfReady()) return;
      if (mountRetryAttempts >= maxMountRetryAttempts) {
        if (!cancelled) {
          setEditorError(sourceUrl || editorBundleUrl);
          console.error("Failed to mount AAS Editor bundle after retries from", sourceUrl || editorBundleUrl);
        }
        return;
      }
      mountRetryAttempts += 1;
      mountRetryTimeout = window.setTimeout(() => {
        scheduleMountRetry(sourceUrl || editorBundleUrl);
      }, 160);
    };

    ensureCss();

    if (mountIfReady()) {
      return () => {
        cancelled = true;
        if (mountRetryTimeout) {
          window.clearTimeout(mountRetryTimeout);
        }
        if (editorMountRef.current?.unmount) {
          editorMountRef.current.unmount();
          editorMountRef.current = null;
        }
      };
    }

    const existingScript = document.querySelector('script[data-editor-bundle="true"]');
    if (existingScript) {
      const onLoad = () => {
        scheduleMountRetry(editorBundleUrl);
      };
      const onError = () => {
        if (cancelled) return;
        setEditorError(editorBundleUrl);
        console.error("Failed to load AAS Editor bundle script from", editorBundleUrl);
      };
      existingScript.addEventListener("load", onLoad);
      existingScript.addEventListener("error", onError);
      scheduleMountRetry(editorBundleUrl);
      return () => {
        cancelled = true;
        if (mountRetryTimeout) {
          window.clearTimeout(mountRetryTimeout);
        }
        existingScript.removeEventListener("load", onLoad);
        existingScript.removeEventListener("error", onError);
        if (editorMountRef.current?.unmount) {
          editorMountRef.current.unmount();
          editorMountRef.current = null;
        }
      };
    }

    const script = document.createElement("script");
    script.src = editorBundleUrl;
    script.async = true;
    script.dataset.editorBundle = "true";
    const onScriptLoad = () => {
      scheduleMountRetry(editorBundleUrl);
    };
    const onScriptError = () => {
      if (cancelled) return;
      setEditorError(editorBundleUrl);
      console.error("Failed to load AAS Editor bundle script from", editorBundleUrl);
    };
    script.addEventListener("load", onScriptLoad);
    script.addEventListener("error", onScriptError);
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      if (mountRetryTimeout) {
        window.clearTimeout(mountRetryTimeout);
      }
      script.removeEventListener("load", onScriptLoad);
      script.removeEventListener("error", onScriptError);
      if (editorMountRef.current?.unmount) {
        editorMountRef.current.unmount();
        editorMountRef.current = null;
      }
    };
  }, [activeService, editorBundleUrl, editorApiUrl, editorCssUrl, idpUrl]);

  useEffect(() => {
    if (activeService !== "editor") return;
    const mount = editorMountRef.current;
    if (!mount?.setAuth) return;
    const isLocalDevHost =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const devUserId =
      userInfo?.email || userInfo?.id || userInfo?.username || "adx-hub-user";
    const bearerToken = editorEmbedAuthToken || undefined;
    mount.setAuth({
      bearerToken,
      devUserId: bearerToken || !isLocalDevHost ? undefined : devUserId,
    });
  }, [activeService, editorEmbedAuthToken, editorEmbedDevIdentity]);

  const showIdentityPanel = activeService === AUTH_PANEL_SERVICE_ID || activeService === "idp";
  const showIdentityWorkspace = showIdentityPanel && isAuthenticated;
  const hideTopbarLoginButton = !isAuthenticated && showIdentityPanel;
  const idpPortalUrl = `${idpUrl.replace(/\/$/, "")}/dashboard/`;
  const servicesLocked = !isAuthenticated;
  const activeServiceDefinition = services.find((service) => service.id === activeService);
  const activeServiceLabel =
    activeService === "home"
      ? "Home"
      : activeService === "docs"
      ? "Documentation"
      : activeService === "apidocs"
      ? "API Documentation"
      : activeService === SEATS_SERVICE_ID
      ? "Team access"
      : showIdentityPanel
      ? "Your profile"
      : activeServiceDefinition?.title || "Service";
  const cleanIdentityValue = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return "";
    const lowered = text.toLowerCase();
    if (["unknown", "none", "null", "undefined", "anonymous", "user"].includes(lowered)) {
      return "";
    }
    return text;
  };
  const userDisplayName =
    cleanIdentityValue(userInfo?.email) ||
    cleanIdentityValue(userInfo?.preferred_username) ||
    cleanIdentityValue(userInfo?.username) ||
    cleanIdentityValue(userInfo?.name) ||
    cleanIdentityValue([userInfo?.given_name, userInfo?.family_name].filter(Boolean).join(" ")) ||
    cleanIdentityValue(userInfo?.sub) ||
    "Account";

  // Profile rows. Kept separate from userDisplayName, which falls back all the
  // way to a subject id so the header always shows something - a profile row
  // is better omitted than filled with an opaque identifier.
  const profileFullName =
    cleanIdentityValue(userInfo?.name) ||
    cleanIdentityValue([userInfo?.given_name, userInfo?.family_name].filter(Boolean).join(" ")) ||
    cleanIdentityValue([userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" "));
  const profileUsername =
    cleanIdentityValue(userInfo?.preferred_username) ||
    cleanIdentityValue(userInfo?.username);
  const renderServiceLoadError = (serviceName, details) => (
    <div className="flex-1 p-6 md:p-12 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-md"
      >
        <p className="text-[#0a0a0a] font-semibold mb-2">{serviceName} is unavailable</p>
        <p className="text-sm text-[#64748b] mb-2">
          {typeof details === "string" && details.trim()
            ? details
            : "Failed to load the service. Please try again in a moment."}
        </p>
        <p className="text-xs text-[#94a3b8]">If the issue persists, contact platform support.</p>
      </motion.div>
    </div>
  );

  return (
    <div className="dashboard-shell min-h-screen flex flex-col">
      <header className="dashboard-topbar sticky top-0 z-50 flex h-16 shrink-0 items-center border-b px-3 sm:px-4 md:px-6 xl:px-8">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#0028CD] md:hidden"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <Link to="/" className="flex items-center" aria-label="ADX Hub home">
              <img
                src="/static/css/assets/img/IDTA_Logo_Blue_Web_M.png"
                alt="IDTA Logo"
                className="h-7 md:h-8"
              />
            </Link>
          </div>

          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2 md:gap-3">
            {isAuthenticated ? (
              <>
                <div className="dashboard-user-chip flex items-center gap-2 rounded-full border py-1 pl-1 pr-2.5 md:gap-3 md:pr-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0028CD]">
                    <User size={14} className="text-white" strokeWidth={2.25} />
                  </div>
                  <span className="hidden max-w-[160px] truncate text-[12.5px] font-medium text-slate-700 sm:inline md:max-w-[200px] md:text-[13px]">
                    {userDisplayName}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="dashboard-ghost-btn inline-flex h-9 items-center gap-1.5 rounded-xl border px-2.5 text-[12.5px] font-semibold sm:px-3 md:px-4"
                >
                  <LogOut size={14} strokeWidth={2.25} />
                  <span className="hidden md:inline">Logout</span>
                </button>
              </>
            ) : hideTopbarLoginButton ? null : (
              <button
                onClick={handleLogin}
                className="dashboard-login-btn inline-flex h-9 items-center gap-1.5 rounded-xl border px-3 text-[12.5px] font-semibold sm:px-4"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      <div className={`dashboard-layout flex flex-1 ${isDesktopViewport && !sidebarOpen ? "sidebar-collapsed" : ""}`}>
        <aside
          className={`dashboard-sidebar border-r shrink-0 sticky top-16 h-[calc(100vh-4rem)] hidden md:flex flex-col overflow-hidden ${
            sidebarOpen ? "is-open" : "is-closed"
          }`}
          aria-hidden={!sidebarOpen}
        >
          <nav className="dashboard-sidebar-scroll flex-1 overflow-y-auto p-4">
            <div className="dashboard-sidebar-home-row mb-2 flex items-center gap-2">
              <motion.button
                onClick={() => setActiveService('home')}
                title="Home"
                className={`dashboard-nav-btn dashboard-nav-btn--home flex-1 flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeService === 'home' 
                    ? 'dashboard-nav-btn--active bg-[#0028CD] text-white' 
                    : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Home size={20} />
                <span className="dashboard-nav-label font-medium">Home</span>
              </motion.button>
              <button
                type="button"
                onClick={() => setSidebarOpen((open) => !open)}
                className="dashboard-sidebar-inline-toggle inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#0028CD]"
                aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
                aria-expanded={sidebarOpen}
                title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              >
                {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </button>
            </div>

            <div className="mt-6 mb-2">
              <p className="dashboard-sidebar-section-title text-xs font-semibold text-[#94a3b8] uppercase tracking-wider px-4 mb-1">Services</p>
              {servicesLocked ? (
                <p className="dashboard-sidebar-lock-note px-4 mb-3 text-[11px] font-medium text-[#64748b]">Locked until sign in</p>
              ) : (
                <div className="mb-3" />
              )}
              <div className="space-y-1">
                {services.map((service) => {
                  const isActive = activeService === service.id;
                  return (
                    <motion.button
                      key={service.id}
                      title={service.title}
                      disabled={servicesLocked}
                      onClick={() => handleServiceClick(service)}
                      className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${
                        servicesLocked ? 'cursor-not-allowed opacity-55' :
                        isActive 
                          ? 'dashboard-nav-btn--active bg-[#0028CD] text-white' 
                          : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                      }`}
                      whileHover={servicesLocked ? undefined : { scale: 1.02, x: 4 }}
                      whileTap={servicesLocked ? undefined : { scale: 0.98 }}
                    >
                      {renderServiceNavIcon(service, isActive)}
                      <span className="dashboard-nav-label font-medium flex-1 text-left">{service.title}</span>
                      {servicesLocked ? (
                        <Lock size={16} className="dashboard-nav-trailing text-slate-400" />
                      ) : (
                        <ExternalLink size={16} className="dashboard-nav-trailing opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 mb-2">
              <p className="dashboard-sidebar-section-title text-xs font-semibold text-[#94a3b8] uppercase tracking-wider px-4 mb-3">Resources</p>
              <motion.button
                title="Team access"
                disabled={servicesLocked}
                onClick={handleSeatsClick}
                className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  servicesLocked ? 'cursor-not-allowed opacity-55 text-[#64748b]' :
                  activeService === SEATS_SERVICE_ID
                    ? 'dashboard-nav-btn--active bg-[#0028CD] text-white'
                    : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                }`}
                whileHover={servicesLocked ? undefined : { scale: 1.02, x: 4 }}
                whileTap={servicesLocked ? undefined : { scale: 0.98 }}
              >
                <Users size={20} />
                <span className="dashboard-nav-label font-medium flex-1 text-left">Team access</span>
                {servicesLocked ? <Lock size={16} className="dashboard-nav-trailing text-slate-400" /> : null}
              </motion.button>
              <motion.button
                title="Documentation"
                disabled={servicesLocked}
                onClick={handleDocsClick}
                className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  servicesLocked ? 'cursor-not-allowed opacity-55 text-[#64748b]' :
                  activeService === 'docs'
                    ? 'dashboard-nav-btn--active bg-[#0028CD] text-white'
                    : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                }`}
                whileHover={servicesLocked ? undefined : { scale: 1.02, x: 4 }}
                whileTap={servicesLocked ? undefined : { scale: 0.98 }}
              >
                <BookOpen size={20} />
                <span className="dashboard-nav-label font-medium flex-1 text-left">Documentation</span>
                {servicesLocked ? <Lock size={16} className="dashboard-nav-trailing text-slate-400" /> : null}
              </motion.button>
              <motion.button
                title="API Docs"
                disabled={servicesLocked}
                onClick={handleApiDocsClick}
                className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  servicesLocked ? 'cursor-not-allowed opacity-55 text-[#64748b]' :
                  activeService === 'apidocs'
                    ? 'dashboard-nav-btn--active bg-[#0028CD] text-white'
                    : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                }`}
                whileHover={servicesLocked ? undefined : { scale: 1.02, x: 4 }}
                whileTap={servicesLocked ? undefined : { scale: 0.98 }}
              >
                <Code2 size={20} />
                <span className="dashboard-nav-label font-medium flex-1 text-left">API Docs</span>
                {servicesLocked ? <Lock size={16} className="dashboard-nav-trailing text-slate-400" /> : null}
              </motion.button>
            </div>
          </nav>
        </aside>

        {sidebarOpen && !isDesktopViewport && (
          <div 
            className="fixed inset-0 bg-black/20 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <motion.aside
          initial={false}
          animate={{ 
            x: sidebarOpen ? 0 : '-100%'
          }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="dashboard-sidebar fixed left-0 top-16 bottom-0 w-[86vw] max-w-[320px] border-r z-50 flex flex-col md:hidden"
        >
          <nav className="flex-1 overflow-y-auto px-4 pb-4 pt-2">
            <motion.button
              onClick={() => {
                setActiveService('home');
                setSidebarOpen(false);
              }}
              className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-all ${
                activeService === 'home' 
                  ? 'bg-[#0028CD] text-white' 
                  : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
              }`}
            >
              <Home size={20} />
              <span className="font-medium">Home</span>
            </motion.button>

            <div className="mt-6 mb-2">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider px-4 mb-1">Services</p>
              {servicesLocked ? (
                <p className="px-4 mb-3 text-[11px] font-medium text-[#64748b]">Locked until sign in</p>
              ) : (
                <div className="mb-3" />
              )}
              <div className="space-y-1">
                {services.map((service) => {
                  const isActive = activeService === service.id;
                  return (
                    <motion.button
                      key={service.id}
                      disabled={servicesLocked}
                      onClick={() => {
                        handleServiceClick(service);
                        setSidebarOpen(false);
                      }}
                      className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        servicesLocked ? 'cursor-not-allowed opacity-55' :
                        isActive 
                          ? 'bg-[#0028CD] text-white' 
                          : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                      }`}
                    >
                      {renderServiceNavIcon(service, isActive)}
                      <span className="font-medium flex-1 text-left">{service.title}</span>
                      {servicesLocked ? <Lock size={16} className="text-slate-400" /> : <ExternalLink size={16} />}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 mb-2">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider px-4 mb-3">Resources</p>
              <motion.button
                onClick={() => {
                  handleDocsClick();
                  setSidebarOpen(false);
                }}
                disabled={servicesLocked}
                className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  servicesLocked ? 'cursor-not-allowed opacity-55 text-[#64748b]' :
                  activeService === 'docs'
                    ? 'bg-[#0028CD] text-white'
                    : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                }`}
              >
                <BookOpen size={20} />
                <span className="font-medium flex-1 text-left">Documentation</span>
                {servicesLocked ? <Lock size={16} className="text-slate-400" /> : null}
              </motion.button>
              <motion.button
                onClick={() => {
                  handleApiDocsClick();
                  setSidebarOpen(false);
                }}
                disabled={servicesLocked}
                className={`dashboard-nav-btn w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  servicesLocked ? 'cursor-not-allowed opacity-55 text-[#64748b]' :
                  activeService === 'apidocs'
                    ? 'bg-[#0028CD] text-white'
                    : 'text-[#64748b] hover:bg-[#f8f9fc] hover:text-[#0028CD]'
                }`}
              >
                <Code2 size={20} />
                <span className="font-medium flex-1 text-left">API Docs</span>
                {servicesLocked ? <Lock size={16} className="text-slate-400" /> : null}
              </motion.button>
            </div>
          </nav>
        </motion.aside>

        <main className={`dashboard-main-content flex-1 min-w-0 min-h-0 overflow-hidden ${isDesktopViewport && !sidebarOpen ? "dashboard-main-content--sidebar-collapsed" : ""}`}>
          <div
            className={`dashboard-transition-indicator ${isServiceSwitching ? "is-visible" : ""}`}
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="dashboard-transition-indicator-dot" />
            <span>Opening {activeServiceLabel}</span>
          </div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.section
              key={activeService}
              className="dashboard-main-stage h-full min-h-0"
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 12 }}
              animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: -10 }}
              transition={{ duration: prefersReducedMotion ? 0.1 : 0.24, ease: [0.22, 1, 0.36, 1] }}
            >
          {activeService === "home" ? (
            <div className={`dashboard-home mx-auto w-full max-w-[1440px] p-4 sm:p-5 md:p-8 xl:p-10 ${isDesktopViewport && !sidebarOpen ? "dashboard-home--expanded" : ""}`}>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="dashboard-hero-panel"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0028CD]">
                  Overview
                </p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-[#0028CD] md:text-4xl">
                  {isAuthenticated
                    ? `Welcome back${userInfo?.first_name ? `, ${userInfo.first_name}` : ""}`
                    : "Welcome to ADX Hub"}
                </h1>
                <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-[#64748b]">
                  Your workspace for AAS-based data exchange.
                </p>
              </motion.div>

              <div className="mt-8 sm:mt-10 flex items-end justify-between">
                <div>
                  <h2 className="text-xl font-bold tracking-tight text-[#0f2c8a]">
                    Platform services
                  </h2>
                  <p className="mt-1 text-[13.5px] text-[#64748b]">
                    Open a service to start working. Embedded services launch directly in your dashboard.
                  </p>
                </div>
              </div>

              <div className={`dashboard-service-grid mt-5 grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 ${isDesktopViewport && !sidebarOpen ? "dashboard-service-grid--expanded" : ""}`}>
                {services.map((service, index) => {
                  const embedInDashboard = ["aas-viewer", "discovery", "validation", "smt", "editor", "idp"].includes(service.id);
                  const isLocked = servicesLocked && embedInDashboard;
                  const Wrapper = embedInDashboard ? motion.button : motion.a;
                  const wrapperProps = embedInDashboard
                    ? { type: "button", onClick: () => handleServiceClick(service), disabled: isLocked }
                    : { href: service.link, target: "_blank", rel: "noopener noreferrer" };
                  return (
                    <Wrapper
                      key={service.id}
                      {...wrapperProps}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.05, duration: 0.35 }}
                      className={`dashboard-service-card group flex min-h-[220px] w-full flex-col rounded-xl border p-6 text-left duration-200 md:min-h-[240px] md:p-7 ${
                        isLocked ? "cursor-not-allowed opacity-70" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="dashboard-service-icon flex h-14 w-14 items-center justify-center rounded-xl">
                          <img
                            src={service.iconImage}
                            alt=""
                            aria-hidden="true"
                            className="h-8 w-8 object-contain"
                          />
                        </div>
                        <span className="dashboard-service-arrow flex h-8 w-8 items-center justify-center rounded-full border text-slate-400 transition-colors group-hover:border-[#0028CD] group-hover:text-[#0028CD]">
                          {isLocked ? (
                            <Lock size={14} strokeWidth={2.5} />
                          ) : embedInDashboard ? (
                            <ChevronRight size={15} strokeWidth={2.5} />
                          ) : (
                            <ArrowUpRight size={15} strokeWidth={2.5} />
                          )}
                        </span>
                      </div>
                      <h3 className="mt-6 text-[16px] font-semibold text-[#0028CD]">
                        {service.title}
                      </h3>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-[#64748b]">
                        {service.description}
                      </p>
                    </Wrapper>
                  );
                })}
              </div>

              <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
                {[
                  {
                    icon: BookOpen,
                    title: "Documentation",
                    description: "Guides for onboarding, API reference, and AAS standards.",
                    action: handleDocsClick,
                    isInternal: true,
                    isLocked: servicesLocked,
                  },
                  {
                    icon: LifeBuoy,
                    title: "Support",
                    description: "Talk to our team for integration help or custom scope.",
                    href: "/#contact",
                    isInternal: false,
                    isLocked: false,
                  },
                ].map(({ icon: Icon, title, description, href, action, isInternal, isLocked }) => {
                  const Wrapper = isInternal ? "button" : "a";
                  const wrapperProps = isInternal
                    ? { type: "button", onClick: action, disabled: isLocked }
                    : { href, target: "_blank", rel: "noopener noreferrer" };
                  return (
                    <Wrapper
                      key={title}
                      {...wrapperProps}
                      className={`dashboard-resource-card group flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                        isLocked ? "cursor-not-allowed opacity-70" : ""
                      }`}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[rgba(0,40,205,0.06)] text-[#0028CD]">
                        <Icon size={16} strokeWidth={2.25} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13.5px] font-semibold text-[#0f172a] group-hover:text-[#0028CD]">
                          {title}
                        </p>
                        <p className="mt-0.5 text-[12.5px] leading-relaxed text-[#64748b]">
                          {description}
                        </p>
                      </div>
                      {isInternal ? (
                        isLocked ? (
                          <Lock
                            size={14}
                            className="mt-1 shrink-0 text-slate-400"
                            strokeWidth={2.25}
                          />
                        ) : (
                          <ChevronRight
                            size={14}
                            className="mt-1 shrink-0 text-slate-400 group-hover:text-[#0028CD]"
                            strokeWidth={2.25}
                          />
                        )
                      ) : (
                        <ArrowUpRight
                          size={14}
                          className="mt-1 shrink-0 text-slate-400 group-hover:text-[#0028CD]"
                          strokeWidth={2.25}
                        />
                      )}
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          ) : activeService === "docs" ? (
            <div className="flex h-full min-h-0 w-full flex-col bg-white">
              <iframe
                title="ADX Hub Documentation"
                src="/docs/?embedded=1"
                className="h-full min-h-0 w-full flex-1 border-0"
              />
            </div>
          ) : activeService === SEATS_SERVICE_ID ? (
            <div className="h-full min-h-0 w-full overflow-y-auto bg-white p-6 md:p-8">
              <div className="mx-auto w-full max-w-2xl">
                <SeatManager />
              </div>
            </div>
          ) : activeService === "apidocs" ? (
            <div className="flex h-full min-h-0 w-full flex-col bg-white">
              <iframe
                title="ADX Hub API Documentation"
                src="/api-docs/"
                className="h-full min-h-0 w-full flex-1 border-0"
              />
            </div>
          ) : showIdentityPanel ? (
            <div className="flex h-full min-h-0 w-full items-start justify-center bg-[#f6f8fc] p-4 md:items-center md:p-8">
              {showIdentityWorkspace ? (
                <div className="w-full max-w-[560px] rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_10px_28px_rgba(15,23,42,0.08)] md:p-6">
                  {/* Titled for the person reading it, not for the system that
                      serves it - "Identity Service" named the backend, which is
                      not what someone opening their own profile is looking for. */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-semibold tracking-tight text-[#0f172a]">
                        Your profile
                      </h2>
                      <p className="mt-1 text-[13px] text-slate-600">
                        Your account details and sign-in settings.
                      </p>
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-[#0028CD]/20 bg-[#0028CD]/5 px-3 py-1 text-[12px] font-semibold text-[#0028CD]">
                      <ShieldCheck size={14} />
                      Signed in
                    </div>
                  </div>

                  {/* Only rows with a real value are rendered, so a sparse
                      profile reads as short rather than full of blanks. */}
                  <dl className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-[#fbfcff]">
                    {[
                      { label: "Name", value: profileFullName },
                      { label: "Email", value: cleanIdentityValue(userInfo?.email) },
                      { label: "Username", value: profileUsername },
                    ]
                      .filter((row) => row.value)
                      .map((row) => (
                        <div key={row.label} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3">
                          <dt className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#64748b]">
                            {row.label}
                          </dt>
                          <dd className="min-w-0 flex-1 break-all text-[14px] font-medium text-[#0f172a]">
                            {row.value}
                          </dd>
                        </div>
                      ))}
                  </dl>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={refresh}
                      className="inline-flex h-10 items-center justify-center rounded-full border border-[#0028CD]/35 bg-white px-4 text-[13px] font-semibold text-[#0028CD] transition-colors hover:border-[#0028CD] hover:bg-[#f3f6ff]"
                    >
                      Refresh session
                    </button>
                  </div>
                  <div className="dashboard-profile-panel mt-5 rounded-xl border border-slate-200 bg-[#fbfcff] p-4">
                    <h3 className="text-[14px] font-semibold text-[#0f172a]">Manage profile</h3>
                    <p className="mt-1 text-[12.5px] text-slate-600">
                      Change your password for ADX Hub and connected services.
                    </p>
                    {changePasswordError ? (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] font-medium text-red-700">
                        {changePasswordError}
                      </div>
                    ) : null}
                    {changePasswordMessage ? (
                      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] font-medium text-emerald-700">
                        {changePasswordMessage}
                      </div>
                    ) : null}
                    <form onSubmit={submitPasswordChange} className="mt-3 space-y-2.5">
                      <input
                        type="password"
                        value={changePasswordForm.currentPassword}
                        onChange={(event) => handleChangePasswordInput("currentPassword", event.target.value)}
                        placeholder="Current password"
                        autoComplete="current-password"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] text-[#0f172a] outline-none transition-colors focus:border-[#0028CD]"
                        required
                      />
                      <input
                        type="password"
                        value={changePasswordForm.newPassword}
                        onChange={(event) => handleChangePasswordInput("newPassword", event.target.value)}
                        placeholder="New password"
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] text-[#0f172a] outline-none transition-colors focus:border-[#0028CD]"
                        required
                      />
                      <input
                        type="password"
                        value={changePasswordForm.confirmPassword}
                        onChange={(event) => handleChangePasswordInput("confirmPassword", event.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] text-[#0f172a] outline-none transition-colors focus:border-[#0028CD]"
                        required
                      />
                      <button
                        type="submit"
                        disabled={changePasswordSubmitting}
                        className="dashboard-auth-submit-btn mt-1 inline-flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        <span className="font-semibold">
                          {changePasswordSubmitting ? "Updating password..." : "Update password"}
                        </span>
                      </button>
                    </form>
                  </div>
                </div>
              ) : (
                <div className="dashboard-login-panel w-full max-w-[430px] p-4 text-left md:p-6">
                  <h2 className="dashboard-login-title">Welcome back</h2>
                  <p className="dashboard-login-subtitle">Login to your account</p>

                  <div className="dashboard-login-toggle mb-4 grid h-[58px] grid-cols-2 p-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setIdpAuthMethod("basic");
                        setAuthPanelError("");
                      }}
                      className={`dashboard-login-toggle-btn inline-flex h-full items-center justify-center gap-2 px-3 text-[14px] font-semibold ${
                        idpAuthMethod === "basic" ? "is-active" : ""
                      }`}
                    >
                      <Lock size={15} />
                      Basic Auth
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIdpAuthMethod("microsoft");
                        setAuthPanelError("");
                      }}
                      className={`dashboard-login-toggle-btn inline-flex h-full items-center justify-center gap-2 px-3 text-[14px] font-semibold ${
                        idpAuthMethod === "microsoft" ? "is-active" : ""
                      }`}
                    >
                      <span className="inline-grid h-3.5 w-3.5 grid-cols-2 gap-[1px]" aria-hidden="true">
                        <span className="bg-[#F25022]" />
                        <span className="bg-[#7FBA00]" />
                        <span className="bg-[#00A4EF]" />
                        <span className="bg-[#FFB900]" />
                      </span>
                      Microsoft
                    </button>
                  </div>

                  {idpAuthMethod === "basic" ? (
                    <div>
                      <form method="post" action={basicAuthActionUrl}>
                        <input type="hidden" name="provider" value="basic" />
                        <input type="hidden" name="next" value={authReturnTarget} />

                        {authPanelError ? (
                          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] font-medium text-red-700">
                            {authPanelError}
                          </div>
                        ) : null}

                        <div className="dashboard-login-field mb-3 flex w-full items-center px-4 py-2.5">
                          <User size={15} className="text-slate-500" />
                          <input
                            id="idp-basic-username"
                            name="username"
                            type="text"
                            value={idpUsername}
                            onChange={(event) => {
                              setIdpUsername(event.target.value);
                              setAuthPanelError("");
                            }}
                            className="dashboard-login-input w-full bg-transparent px-2.5 text-[14px] text-[#0f172a] outline-none"
                            placeholder="Enter your username"
                            autoComplete="username"
                            required
                          />
                        </div>

                        <div className="dashboard-login-field mb-4 flex w-full items-center px-4 py-2.5">
                          <Lock size={15} className="text-slate-500" />
                          <input
                            id="idp-basic-password"
                            name="password"
                            type={idpShowPassword ? "text" : "password"}
                            value={idpPassword}
                            onChange={(event) => {
                              setIdpPassword(event.target.value);
                              setAuthPanelError("");
                            }}
                            className="dashboard-login-input w-full bg-transparent px-2.5 text-[14px] text-[#0f172a] outline-none"
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setIdpShowPassword((prev) => !prev)}
                            className="dashboard-login-eye-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500"
                            aria-label={idpShowPassword ? "Hide password" : "Show password"}
                          >
                            {idpShowPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>

                        <button
                          type="submit"
                          className="dashboard-login-submit-btn mb-2 inline-flex w-full items-center justify-center gap-2"
                        >
                          <LogIn size={16} />
                          <span className="font-semibold">Log in</span>
                        </button>
                      </form>
                    </div>
                  ) : (
                    <a
                      href={microsoftAuthUrl}
                      className="dashboard-login-microsoft-btn inline-flex w-full items-center justify-center gap-2 py-2.5 text-[#0028CD]"
                    >
                      <span className="inline-grid h-3.5 w-3.5 grid-cols-2 gap-[1px]" aria-hidden="true">
                        <span className="bg-[#F25022]" />
                        <span className="bg-[#7FBA00]" />
                        <span className="bg-[#00A4EF]" />
                        <span className="bg-[#FFB900]" />
                      </span>
                      <span className="font-semibold">Log in with Microsoft</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : activeService === "smt" ? (
            <div className="flex-1 flex flex-col bg-[#f8f9fc] min-h-0">
              {!smtLoaded && !smtError && (
                <div className="p-6 md:p-12 flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0028CD] mx-auto mb-4 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[#0a0a0a] font-medium mb-1">Loading Submodel Template Repository</p>
                    <p className="text-sm text-[#64748b]">Please wait while the service is initialized.</p>
                  </motion.div>
                </div>
              )}
              {smtError && (
                renderServiceLoadError(activeServiceDefinition?.title || "Service", smtError)
              )}
              <div ref={smtContainerRef} className="smt-embed flex-1 w-full min-h-0 overflow-auto" />
            </div>
          ) : activeService === "discovery" ? (
            <div className="flex-1 bg-white">
              {!discoveryLoaded && !discoveryError && (
                <div className="p-6 md:p-12 flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0028CD] mx-auto mb-4 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[#0a0a0a] font-medium mb-1">Loading Discover Data Providers</p>
                    <p className="text-sm text-[#64748b]">Please wait while the service is initialized.</p>
                  </motion.div>
                </div>
              )}
              {discoveryError && (
                renderServiceLoadError(activeServiceDefinition?.title || "Service", discoveryError)
              )}
              <div ref={discoveryContainerRef} className="discovery-embed-container w-full h-full min-h-0 overflow-auto" />
            </div>
          ) : activeService === "validation" ? (
            <div className="flex-1 flex flex-col bg-[#fafafa] min-h-0">
              {!validationLoaded && !validationError && (
                <div className="flex-1 flex items-center justify-center px-6">
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                  >
                    <div className="w-14 h-14 rounded-full bg-[#0028CD] mx-auto mb-5 flex items-center justify-center">
                      <div className="w-6 h-6 border-[2.5px] border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[22px] leading-[1.2] text-[#0a0a0a] font-semibold mb-2">Loading AAS Validation Service</p>
                    <p className="text-[14px] leading-[1.45] text-[#64748b]">Please wait while the service is initialized.</p>
                  </motion.div>
                </div>
              )}
              {validationError && (
                renderServiceLoadError(activeServiceDefinition?.title || "Service")
              )}
              <div
                ref={validationContainerRef}
                className="validation-embed flex-1 w-full min-h-0 overflow-auto"
                style={{ minHeight: validationLoaded ? "100%" : 0 }}
              />
            </div>
          ) : activeService === "editor" ? (
            <div className="flex-1 flex flex-col bg-[#f8f9fc] min-h-0">
              {!editorLoaded && !editorError && (
                <div className="flex-1 p-6 md:p-12 flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0028CD] mx-auto mb-4 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[#0a0a0a] font-medium mb-1">Loading AAS Editor</p>
                    <p className="text-sm text-[#64748b]">Please wait while the service is initialized.</p>
                  </motion.div>
                </div>
              )}
              {editorError && (
                renderServiceLoadError(activeServiceDefinition?.title || "Service")
              )}
              <div
                ref={editorContainerRef}
                className="editor-embed flex-1 w-full min-h-0 overflow-auto"
                style={{ minHeight: editorLoaded ? "100%" : 0 }}
              />
            </div>
          ) : activeService === "aas-viewer" ? (
            <div className="flex-1 flex flex-col bg-[#f8f9fc]">
              {!discoveryClientLoaded && !discoveryClientError && (
                <div className="flex-1 p-6 md:p-12 flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0028CD] mx-auto mb-4 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[#0a0a0a] font-medium mb-1">Loading Discover AAS</p>
                    <p className="text-sm text-[#64748b]">Please wait while the service is initialized.</p>
                  </motion.div>
                </div>
              )}
              {discoveryClientError && (
                renderServiceLoadError(activeServiceDefinition?.title || "Service", discoveryClientError)
              )}
              <div ref={discoveryClientContainerRef} className="discovery-client-embed flex-1 w-full min-h-0 overflow-auto" />
            </div>
          ) : (
            <div className="p-6 md:p-12 flex items-center justify-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-xl w-full text-center"
              >
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#f8f9fc] flex items-center justify-center">
                    {React.createElement(activeServiceDefinition?.icon || Home, {
                      size: 28,
                      className: "text-[#0028CD]",
                    })}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#0a0a0a] mb-1">
                      {activeServiceDefinition?.title || "Service"}
                    </h2>
                    <p className="text-[#64748b]">
                      {activeServiceDefinition?.description ||
                        "Service in the ecosystem."}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (activeServiceDefinition?.link) {
                      window.open(activeServiceDefinition.link, '_blank', "noopener,noreferrer");
                    }
                  }}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Open {activeServiceDefinition?.title || "Service"}
                  <ExternalLink size={18} />
                </button>
              </motion.div>
            </div>
          )}
            </motion.section>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
